import {
    Box,
    Text,
    Flex,
    CloseButton,
    Spinner,
    Center,
    HStack,
} from '@chakra-ui/react';
import React, { useEffect, useMemo, useState } from 'react';
import { useGetInventoryActivityLogs } from '../../hooks/inventory/useGetInventoryActivityLogs';
import {
    INVENTORY_EVENT_TYPE_COLORS,
    INVENTORY_EVENT_TYPE_LABELS,
    groupInventoryLogsByDate,
    renderInventoryLogCard,
} from '../../components/inventory/inventoryActivityLogShared';
import { INSTANT_SIDE_DRAWER_MOTION_PROPS } from '../sideDrawerContainerProps';
import Timeline, { TimelineGroupData } from '../../components/Timeline';
import AppDrawer from '../../components/AppDrawer';
import {
    ActivityFeedFilters,
    EMPTY_ACTIVITY_FEED_FILTERS,
    hasActiveActivityFeedFilters,
    useActivityFeedFilterUsers,
    type ActivityFeedFiltersValue,
} from '../../components/activityFeedFilters';
import { Button, TextField } from '../../ui';

const TYPE_FILTER_OPTIONS = Object.entries(INVENTORY_EVENT_TYPE_LABELS).map(([value, label]) => ({ value, label }));
const PAGE_SIZE = 50;

interface InventoryItemActivityContentProps {
    inventoryItemId: string;
    enabled?: boolean;
    /** When true, render search/filters above the timeline (standalone drawer). */
    showFilters?: boolean;
}

export const InventoryItemActivityContent: React.FC<InventoryItemActivityContentProps> = ({
    inventoryItemId,
    enabled = true,
    showFilters = true,
}) => {
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [page, setPage] = useState(1);
    const [filters, setFilters] = useState<ActivityFeedFiltersValue>({ ...EMPTY_ACTIVITY_FEED_FILTERS });
    const filterUsers = useActivityFeedFilterUsers();

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch((prev) => {
                if (prev !== search.trim()) setPage(1);
                return search.trim();
            });
        }, 500);
        return () => clearTimeout(timer);
    }, [search]);

    const { data, isLoading, isError, isFetching } = useGetInventoryActivityLogs(
        inventoryItemId,
        page,
        PAGE_SIZE,
        enabled,
        debouncedSearch,
        filters,
    );

    const logs = useMemo(() => data?.logs ?? [], [data?.logs]);
    const totalPages = Math.max(1, Math.ceil((data?.totalCount ?? 0) / PAGE_SIZE));

    const handleFiltersChange = (next: ActivityFeedFiltersValue) => {
        setFilters(next);
        setPage(1);
    };

    const timelineGroups: TimelineGroupData[] = useMemo(() => {
        const grouped = groupInventoryLogsByDate(logs);
        return Array.from(grouped.entries()).map(([label, items]) => ({
            key: label,
            label,
            rightLabel: `${items.length}`,
            items: items.map((log) => ({
                key: log._id,
                nodeColor: INVENTORY_EVENT_TYPE_COLORS[log.eventType] ?? 'brand.solid',
                content: renderInventoryLogCard(log, { flat: true }),
            })),
        }));
    }, [logs]);

    return (
        <Flex direction="column" h="100%" minH={0}>
            {showFilters && (
                <Box pb={3} flexShrink={0}>
                    <ActivityFeedFilters
                        filters={filters}
                        onFiltersChange={handleFiltersChange}
                        eventTypeOptions={TYPE_FILTER_OPTIONS}
                        users={filterUsers}
                        searchSlot={(
                            <TextField
                                size="sm"
                                placeholder="Search activity…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                h="32px"
                            />
                        )}
                    />
                </Box>
            )}
            <Box flex={1} overflowY="auto" minH={0}>
                {isLoading && (
                    <Center py={12}>
                        <Spinner color="brand.solid" />
                    </Center>
                )}
                {isError && (
                    <Text fontSize="sm" color="red.500" textAlign="center" py={8}>
                        Failed to load activity
                    </Text>
                )}
                {!isLoading && !isError && logs.length === 0 && (
                    <Text
                        fontSize="sm"
                        color="fg.muted"
                        fontStyle="italic"
                        textAlign="center"
                        py={8}
                    >
                        {debouncedSearch || hasActiveActivityFeedFilters(filters)
                            ? 'No activity matches your filters'
                            : 'No activity for this part yet'}
                    </Text>
                )}
                {!isLoading && !isError && logs.length > 0 && (
                    <Timeline groups={timelineGroups} />
                )}
            </Box>
            {totalPages > 1 && (
                <HStack
                    justify="center"
                    spacing={3}
                    py={2}
                    flexShrink={0}
                    borderTop="1px solid"
                    borderColor="border.subtle"
                >
                    <Button
                        size="xs"
                        variant="outlineGray"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        isDisabled={page <= 1 || isFetching}
                    >
                        Previous
                    </Button>
                    <Text fontSize="xs" color="fg.muted">
                        Page {page} of {totalPages}
                    </Text>
                    <Button
                        size="xs"
                        variant="outlineGray"
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        isDisabled={page >= totalPages || isFetching}
                    >
                        Next
                    </Button>
                </HStack>
            )}
        </Flex>
    );
};

interface InventoryItemActivityPanelProps {
    inventoryItemId: string;
    onClose: () => void;
    /** When false, skip fetching (e.g. panel not mounted). Default true. */
    enabled?: boolean;
}

/** Embedded side panel (e.g. inside EditInventoryItemDrawer). */
export const InventoryItemActivityPanel: React.FC<InventoryItemActivityPanelProps> = ({
    inventoryItemId,
    onClose,
    enabled = true,
}) => (
    <Flex direction="column" h="100%" bg="white">
        <Flex
            align="center"
            justify="space-between"
            px={5}
            h="topbar"
            flexShrink={0}
            borderBottom="1px solid"
            borderColor="border.subtle"
            bg="white"
        >
            <Text fontSize="sm" fontWeight="semibold" color="fg.default">
                Item Activity
            </Text>
            <CloseButton onClick={onClose} size="sm" />
        </Flex>
        <Box flex={1} overflow="hidden" px={5} py={4} bg="white" minH={0}>
            <InventoryItemActivityContent inventoryItemId={inventoryItemId} enabled={enabled} />
        </Box>
    </Flex>
);

interface InventoryItemActivityDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    inventoryItemId: string;
}

/** Standalone drawer wrapper (e.g. if opened outside the edit drawer). */
const InventoryItemActivityDrawer: React.FC<InventoryItemActivityDrawerProps> = ({
    isOpen,
    onClose,
    inventoryItemId,
}) => (
    <AppDrawer
        isOpen={isOpen}
        onClose={onClose}
        size="md"
        title="Item Activity"
        contentProps={{
            motionProps: INSTANT_SIDE_DRAWER_MOTION_PROPS,
            overflow: 'hidden',
        }}
        bodyProps={{ px: 5, py: 4, pt: 4, bg: 'bg.surface' }}
    >
        <InventoryItemActivityContent inventoryItemId={inventoryItemId} enabled={isOpen} />
    </AppDrawer>
);

export default InventoryItemActivityDrawer;
